import React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Cpu,
  HeartHandshake,
  Lock,
  Sparkles,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { t, tf } from "@/i18n";

const Row = ({ label, value, tone = "default", testId, icon: Icon }) => {
  const colors = {
    default: "var(--ink)",
    good: "var(--success)",
    warn: "var(--warning)",
    bad: "var(--danger)",
  };
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[color:var(--line)] py-2 last:border-0">
      <span className="flex items-center gap-2 text-[13px] text-[color:var(--ink-soft)]">
        {Icon ? <Icon className="h-3.5 w-3.5" style={{ color: colors[tone] }} /> : null}
        {label}
      </span>
      <span className="font-num text-[13px] font-semibold" style={{ color: colors[tone] }} data-testid={testId}>
        {value}
      </span>
    </div>
  );
};

export const ValidationPanel = ({ plan, guestMap, onFocusGuest }) => {
  const validation = plan?.validation || {};
  const stats = plan?.stats || {};
  const conflicts = [];
  (validation.tables || []).forEach((table) => {
    (table.conflicts || []).forEach(([a, b]) => conflicts.push({ table: table.table, a, b }));
  });
  const overCapacity = validation.over_capacity_tables || [];
  const splitParties = validation.split_parties || [];
  const unseated = plan?.unseated || [];
  const lockedCount = (plan?.tables || []).filter((table) => table.locked).length;
  const pinnedCount = Object.keys(plan?.locks?.pins || {}).length;
  const allGood =
    !conflicts.length && !overCapacity.length && !splitParties.length && !unseated.length;

  return (
    <section data-testid="validation-panel" className="surface rounded-xl p-4" aria-label={t("validation.aria")}>
      <header className="mb-3 flex items-start gap-2">
        {allGood ? (
          <CheckCircle2 className="mt-0.5 h-5 w-5" style={{ color: "var(--success)" }} />
        ) : (
          <AlertTriangle className="mt-0.5 h-5 w-5" style={{ color: "var(--danger)" }} />
        )}
        <div>
          <h2 className="font-display text-lg font-semibold leading-tight">
            {allGood ? t("validation.allGood") : t("validation.toCheck")}
          </h2>
          <p className="text-xs text-[color:var(--ink-soft)]">
            {allGood ? t("validation.allGoodHint") : t("validation.toCheckHint")}
          </p>
        </div>
      </header>

      <Row
        label={t("validation.seated")}
        value={`${stats.seated || 0}/${stats.guests_total || 0}`}
        tone={stats.unseated ? "warn" : "good"}
        testId="validation-seated"
        icon={Users}
      />
      <Row
        label={t("validation.avoidsHonoured")}
        value={`${(stats.avoid_rules || 0) - conflicts.length}/${stats.avoid_rules || 0}`}
        tone={conflicts.length ? "bad" : "good"}
        testId="validation-hard-conflicts-count"
        icon={AlertTriangle}
      />
      <Row
        label={t("validation.wishes")}
        value={`${stats.wishes_satisfied || 0}/${stats.wishes_total || 0}`}
        tone={(stats.wishes_total || 0) === (stats.wishes_satisfied || 0) ? "good" : "warn"}
        testId="validation-wishes"
        icon={HeartHandshake}
      />
      <Row
        label={t("validation.overCapacity")}
        value={overCapacity.length ? overCapacity.join(", ") : t("validation.none")}
        tone={overCapacity.length ? "bad" : "good"}
        testId="validation-over-capacity"
        icon={Users}
      />
      <Row
        label={t("validation.splitParties")}
        value={splitParties.length ? String(splitParties.length) : t("validation.none")}
        tone={splitParties.length ? "warn" : "good"}
        testId="validation-split-parties"
      />
      <Row
        label={t("validation.toDecide")}
        value={String(unseated.reduce((sum, item) => sum + item.guest_ids.length, 0))}
        tone={unseated.length ? "warn" : "good"}
        testId="validation-to-decide"
      />
      <Row label={t("validation.chemistry")} value={(stats.avg_affinity || 0).toFixed(2)} testId="validation-chemistry" />
      {lockedCount || pinnedCount ? (
        <Row
          label={t("validation.kept")}
          value={[
            lockedCount ? tf("validation.tables", { n: lockedCount }) : null,
            pinnedCount ? tf("validation.guests", { n: pinnedCount }) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          testId="validation-locked"
          icon={Lock}
        />
      ) : null}

      {conflicts.length ? (
        <div className="mt-3 rounded-lg border p-2" style={{ borderColor: "var(--danger)" }}>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--danger)" }}>
            {t("validation.conflicts")}
          </p>
          <ul className="space-y-1">
            {conflicts.map(({ table, a, b }) => (
              <li
                key={`${table}-${a}-${b}`}
                className="flex items-center justify-between gap-2 text-[12px]"
                data-testid={`conflict-row-${a}-${b}`}
              >
                <span>
                  {t("overview.table")} {table}: {guestMap[a]?.name || a} &amp; {guestMap[b]?.name || b}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-[11px]"
                  data-testid={`conflict-fix-${a}`}
                  onClick={() => onFocusGuest?.(guestMap[a])}
                >
                  {t("validation.fix")}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {splitParties.length ? (
        <div className="mt-3 rounded-lg border p-2" style={{ borderColor: "var(--warning)" }} data-testid="split-parties-detail">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--warning)" }}>
            {t("validation.splitParties")}
          </p>
          <ul className="space-y-1">
            {splitParties.map((party) => {
              const names = (party.guest_ids || []).map((id) => guestMap[id]?.name || id).join(", ");
              const where = (party.tables || []).length
                ? tf("validation.tablesWord", { list: party.tables.join(", ") })
                : "";
              const parked = (party.parked || []).length
                ? tf("validation.parked", { n: (party.parked || []).length })
                : "";
              return (
                <li
                  key={party.party}
                  className="flex items-center justify-between gap-2 text-[12px]"
                  data-testid={`split-party-row-${party.party}`}
                >
                  <span className="min-w-0">
                    <span className="truncate">{names}</span>
                    <span className="block text-[11px] text-[color:var(--ink-soft)]">
                      {[where, parked].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 shrink-0 px-2 text-[11px]"
                    data-testid={`split-party-fix-${party.party}`}
                    onClick={() => onFocusGuest?.(guestMap[party.guest_ids?.[0]])}
                  >
                    {t("validation.fix")}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {(plan?.warnings || []).length ? (
        <div className="mt-3 rounded-lg surface-2 p-2">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--warning)]">
            {t("validation.remarks")}
          </p>
          <ul className="space-y-1">
            {plan.warnings.slice(0, 5).map((warning, index) => (
              <li key={index} className="text-[11.5px] leading-snug text-[color:var(--ink-soft)]">
                {warning}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <footer className="mt-3 border-t border-[color:var(--line)] pt-2">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[color:var(--muted-ink)]">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" style={{ color: "var(--gild)" }} /> {t("validation.aiRead")}
          </span>
          <span className="inline-flex items-center gap-1">
            <Cpu className="h-3 w-3" style={{ color: "var(--sage-dark)" }} /> {t("validation.codeDid")}
          </span>
        </p>
        <p className="font-num mt-1 text-[10.5px] text-[color:var(--muted-ink)]">
          {plan?.model} · {tf("validation.generatedIn", { s: plan?.runtime_seconds })} ·{" "}
          {tf("validation.groups", { n: stats.parties })} · {tf("validation.responses", { p: stats.response_rate })}
        </p>
      </footer>
    </section>
  );
};

export default ValidationPanel;
